import { createFileRoute } from "@tanstack/react-router";
import { LegalLayout } from "@/components/LegalLayout";
import { useLocale } from "@/contexts/LocaleContext";
import { DICTS } from "@/lib/i18n";
import { PUBLIC_SITE_ORIGIN } from "@/lib/public-origin";

import { publicCacheHeaders } from "@/lib/public-cache-headers";

export const Route = createFileRoute("/terms")({
  headers: publicCacheHeaders,
  head: () => ({
    meta: [
      { title: DICTS.en["legal.terms.meta.title"] },
      { name: "description", content: DICTS.en["legal.terms.meta.description"] },
      { property: "og:title", content: DICTS.en["legal.terms.meta.title"] },
      { property: "og:description", content: DICTS.en["legal.terms.meta.description"] },
      { property: "og:url", content: `${PUBLIC_SITE_ORIGIN}/terms` },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${PUBLIC_SITE_ORIGIN}/terms` }],
  }),
  component: TermsPage,
});

const SECTIONS = [
  "pricing",
  "payment",
  "authenticity",
  "loyalty",
  "ip",
  "disputes",
  "changes",
] as const;

function TermsPage() {
  const { t } = useLocale();

  const bullets = (key: string) =>
    t(key)
      .split("|")
      .map((s) => s.trim())
      .filter(Boolean);

  return (
    <LegalLayout titleKey="legal.terms.title" eyebrowKey="legal.terms.eyebrow">
      <p>{t("legal.terms.intro")}</p>
      <TermsList items={bullets("legal.terms.li_intro")} />
      {SECTIONS.map((s) => (
        <div key={s}>
          <h2>{t(`legal.terms.h2_${s}`)}</h2>
          <TermsList items={bullets(`legal.terms.li_${s}`)} />
        </div>
      ))}
    </LegalLayout>
  );
}

function TermsList({ items }: { items: string[] }) {
  if (items.length === 0) return null;
  return (
    <ul className="mt-4 space-y-2.5">
      {items.map((item) => (
        <li key={item} className="flex gap-3 text-sm leading-relaxed">
          <span
            aria-hidden
            className="mt-[0.55rem] h-1 w-1 shrink-0 rounded-full bg-[var(--rule-accent)]"
          />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

